import { Formik } from 'formik';
import { FC, useEffect } from 'react';
import { Button } from 'src/components/buttons-and-actions/button';
import { Toggle } from 'src/components/buttons-and-actions/toggle';
import { FormContainer } from 'src/components/form/form-container';
import { Label } from 'src/components/form/label';
import { Textarea } from 'src/components/form/textarea';
import { Stack } from 'src/components/layout/stack';
import { Modal } from 'src/components/overlay/modal';
import { Tooltip, useTooltipControl } from 'src/components/overlay/tooltip';
import { useWizardStore } from 'src/components/wizard/use-wizard-store';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { useModal2 } from 'src/hooks/use-modal';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { ParentZIndexProvider } from 'src/hooks/use-parent-z-index';
import { useSafeLocalStorage } from 'src/hooks/use-storage';
import { REGULAR_FCFS_DESIGN_OPT_OUT_KEY } from 'src/pages/transport/fcfs-booking/regular/use-new-fcfs-design';
import {
  FcfsBookingStepsData,
  regularFcfsWizardName,
} from 'src/pages/transport/fcfs-booking/regular/utils';
import { getBreadcrumbHistory } from 'src/utils/breadcrumbs';
import { locationReplace } from 'src/utils/location-usage';
import { reportError } from 'src/utils/report-error';

export function redirectToOldFcfsWizard() {
  locationReplace(`${PRISMA_CONFIG.oldFrontendUrl}/#/fcfs-booking`);
}

export const OptOut: FC = () => {
  const safeLocalStorage = useSafeLocalStorage();
  const wizardStore = useWizardStore<FcfsBookingStepsData>(
    regularFcfsWizardName
  );

  const newDesignTooltipControl = useTooltipControl();
  const oldDesignModal = useModal2({
    modal(props) {
      return (
        <OldDesignModal
          {...props}
          onSuccess={() => {
            wizardStore.clear().then(() => {
              safeLocalStorage.setItem(REGULAR_FCFS_DESIGN_OPT_OUT_KEY, 'true');
              redirectToOldFcfsWizard();
            });
          }}
        />
      );
    },
    trigger(props) {
      return (
        // fake parent z-index needed or else the page header might be on top of the tooltip
        <ParentZIndexProvider value={1}>
          <Tooltip
            content="You can switch back as long as the test phase is live. Please help us to improve our platform and send Your feedback to our Customer Success Team."
            controlActive={newDesignTooltipControl.active}
          >
            {(targetProps) => (
              <Toggle
                label="Deactivate New Design"
                isChecked
                {...props}
                {...targetProps}
                {...newDesignTooltipControl.handlers}
                style={{ display: 'block' }}
              />
            )}
          </Tooltip>
        </ParentZIndexProvider>
      );
    },
  });

  return (
    <Stack flow="column" gap={1} justifyContent="end">
      {oldDesignModal.modal}
      <Label>New Regular FCFS Design</Label>
      {oldDesignModal.trigger}
    </Stack>
  );
};

type OldDesingModalProps = {
  close: () => void;
  onSuccess: () => void;
};

const OldDesignModal: FC<OldDesingModalProps> = ({ close, onSuccess }) => {
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const createFreshDeskTicket = useCreateFreshDeskTicket();

  useEffect(() => {
    // no matter if there was an error or successful response, we'll move on
    if (!createFreshDeskTicket.response && !createFreshDeskTicket.error) return;
    onSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createFreshDeskTicket.response, createFreshDeskTicket.error]);

  return (
    <Modal title="Are you sure you want to switch back?" onDismiss={close}>
      <Formik<{ feedback: string }>
        initialValues={{ feedback: '' }}
        onSubmit={(values) => {
          // only when the user provided feedback (which is only possible when the user
          // is authenticated), we'll create a HelpDesk ticket.
          if (values.feedback && monolithUser) {
            createFreshDeskTicket.execute({
              tags: ['New design feedback'],
              email: 'api+frontend@prisma-capacity.eu',
              name: 'Generated Ticket',
              subject: '[Feedback] Regular FCFS Process Migration',
              description: `The customer **${monolithUser.firstName} ${
                monolithUser.lastName
              }** (${monolithUser.email}) from ${
                monolithUser.isTso ? 'TSO' : 'Shipper'
              } **${
                monolithUser.shipperName ?? monolithUser.tsoName
              }** provided the following feedback:


${values.feedback}


_User UUID: ${monolithUser.userUuid}, Stage: ${PRISMA_CONFIG.stage}


${getBreadcrumbHistory()}`,
            });
          } else {
            onSuccess();
          }
        }}
      >
        <FormContainer mode="regular">
          <p>
            Please, confirm that you want to switch back to the old Regular FCFS
            design.
          </p>

          {monolithUser && (
            <Textarea
              name="feedback"
              label="What would you like to improve?"
              placeholder="Write your feedback here..."
              stacked
            />
          )}

          <Stack flow="column" gap={1} justifyContent="space-between">
            <Button
              mode="secondary"
              onClick={close}
              disabled={createFreshDeskTicket.pending}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              mode="primary"
              disabled={createFreshDeskTicket.pending}
            >
              {createFreshDeskTicket.pending ? 'Saving...' : 'Switch Back'}
            </Button>
          </Stack>
        </FormContainer>
      </Formik>
    </Modal>
  );
};

type TicketData = {
  name: string;
  // note: if you use a real customers mail here, they will be notified about
  // 1) a freshdesk account was created for them (in case it doesn't already exists)
  // 2) a freshdesk ticket was created for them (in case an account already exists)
  // if you want to NOT notify the customers, use a different email
  email: string;
  subject: string;
  description: string;
  tags: string[];
  // responder_id: number; // responder must be part of group
  // group_id: number; // e.g. 36000231833 = Software Development, 36000174215 = Customer Care
  // status?: number;
};

function useCreateFreshDeskTicket() {
  const request = useAxios(
    (axios, signal, data: TicketData) =>
      axios.request<void>({
        signal,
        // https://developers.freshdesk.com/widget-api
        url: 'https://prisma-capacity.freshdesk.com/api/widget/tickets',
        method: 'POST',
        headers: {
          'X-Client-Id': '19c8d14d-aee0-7c27-adb2-e0ff67485b7c',
          'X-Widget-Id': 36000000859,
        },
        data: {
          // mandatory fields:
          form_id: 483,
          product_id: 36000001877, // Prisma Platform
          custom_fields: { cf_users_priority: 'Low' },
          ...data,
        },
      }),
    {
      neededOnPageLoad: false,
      onError(error) {
        if (isServerError(error, null)) {
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  // errors should be reported, but not block the user
  useEffect(() => {
    if (!request.error) return;
    reportError(request.error);
  }, [request.error]);

  return request;
}
